import React, { useState } from 'react';
import ExerciseHeader from "../ExerciseHeader";
import ClickCycleColorsExercise from "./ClickCycleColorsExercise";
import ClickCycleEmojiExercise from "./ClickCycleEmojiExercise";
import CountAndInputExercise from "./CountAndInputExercise";
import MultiSelectExercise from "./MultiSelectExercise";
import PaintColorsExercise from "./PaintColorsExercise";
import SelectOptionExercise from "./SelectOptionExercise";
import ExerciseInstruction from "../ExerciseInstruction";
import { useParams } from 'react-router';
// ===============================================
// 1. SIMPLE MODAL COMPONENT (Placeholder for demonstration)
// ===============================================

/**
 * A simple, attractive modal component for sharing a link.
 * NOTE: For production, you should use a dedicated library or a more robust modal pattern.
 */
const ShareModal = ({ isOpen, onClose, shareData }) => {
    const {id,category} = useParams()
    if (!isOpen) return null;

    // Simulate a dynamic link based on the exercise ID/Title
    const shareLink = `${window.location.origin}/exercise/${category}/${id}`;
    const shareDescription = `لقد أنجزت تمرين: "${shareData.title}"! هل يمكنك تحقيق نفس النتيجة؟`;

    // Function to handle the Web Share API (for mobile/modern browsers)
    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'مشاركة الإنجاز',
                    text: shareDescription,
                    url: shareLink,
                });
                onClose();
            } catch (error) {
                console.error('Error sharing', error);
                // Fallback will still be shown
            }
        }
    };

    // Function to copy link to clipboard (fallback)
    const handleCopy = () => {
        navigator.clipboard.writeText(shareLink);
        alert('تم نسخ الرابط!');
        onClose();
    };

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
            onClick={onClose} // Close on backdrop click
        >
            <div
                className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100 duration-300"
                onClick={e => e.stopPropagation()} // Prevent closing when clicking inside modal
                dir="rtl"
            >
                <h3 className="text-2xl font-bold mb-4 text-green-700">🎉 شارك إنجازك!</h3>

                {/* Description Text */}
                <p className="text-gray-700 mb-4 border-r-4 border-green-500 pr-3">
                    {shareDescription}
                </p>

                {/* Share Link Preview (Attractive Box) */}
                <div className="bg-gray-100 p-3 rounded-lg flex items-center justify-between mb-6">
                    <span className="truncate text-sm font-mono text-gray-600 flex-1">{shareLink}</span>
                    <button
                        onClick={handleCopy}
                        className="bg-green-500 text-white p-2 rounded-lg hover:bg-green-600 transition-colors text-sm font-semibold mr-2"
                    >
                        نسخ
                    </button>
                </div>

                {/* Share Options */}
                <div className="flex flex-col space-y-3">
                    {navigator.share && (
                        <button
                            onClick={handleNativeShare}
                            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-md flex items-center justify-center"
                        >
                            <svg className="w-5 h-5 ml-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.314l4.94 2.47a3 3 0 10.835-1.668L13 11a3 3 0 000-2l.023-.112a3.003 3.003 0 00-.023-.888zM6 13a1 1 0 110-2 1 1 0 010 2zm4-4a1 1 0 11-2 0 1 1 0 012 0zm7-4a1 1 0 110-2 1 1 0 010 2z"></path></svg>
                            مشاركة فورية
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-colors"
                    >
                        إغلاق
                    </button>
                </div>
            </div>
        </div>
    );
};


// ===============================================
// 2. MAIN EXERCISE COMPONENT (Updated)
// ===============================================

const Exercise = ({ exercise }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const ExerciseComponent = {
        'click-cycle-colors': ClickCycleColorsExercise,
        'click-cycle-emoji': ClickCycleEmojiExercise,
        'select-option': SelectOptionExercise,
        'paint-colors': PaintColorsExercise,
        'count-and-input': CountAndInputExercise,
        'multi-select': MultiSelectExercise
    }[exercise.type];

    // Data passed to the ShareModal
    const shareData = {
        id: exercise.file, // Assuming 'file' serves as a unique ID for the exercise link
        title: exercise.title
    };

    return (
        <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-3 sm:p-6 mb-4 sm:mb-6 border-2 sm:border-4 border-gray-200 shadow-lg" dir="rtl">
            <ExerciseHeader title={exercise.title} />
            
            {/* Share Button Section */}
            <div className="mb-4 sm:mb-6 text-left">
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="
                        bg-teal-500 text-white 
                        py-2 px-6 
                        rounded-full 
                        font-semibold 
                        text-base sm:text-lg 
                        shadow-lg 
                        hover:bg-teal-600 
                        focus:outline-none focus:ring-4 focus:ring-teal-300
                        transition duration-300 ease-in-out
                        w-full sm:w-auto 
                        transform hover:scale-105
                    "
                >
                    مشاركة إنجازي 🚀
                </button>
            </div>
            
            <ExerciseInstruction instruction={exercise.instruction} />
            {ExerciseComponent && (
                // Added an attractive style to the exercise child container
                <div className="mt-6 p-4 sm:p-6 bg-white rounded-lg border border-teal-100 shadow-inner">
                    <ExerciseComponent exercise={exercise} />
                </div>
            )}

            {/* The Modal */}
            <ShareModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                shareData={shareData}
            />
        </div>
    );
};

export default Exercise